import dayjs from 'dayjs'
import cx from 'classnames'
import { Fragment, useState, useEffect } from 'react'
import { isNil } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { createPortal } from 'react-dom'

import { st } from 'lib/helpers'
import { Post } from 'lib/_types'
import { useGroupOrThreadOrPostPageData, usePostActions } from 'hooks'
import { logEvent } from 'lib/analytics'
import { useGetPostLikersQuery } from 'store/api/yay/post'
import { Link, matchI18nPath } from 'features/i18n/router'
import { ROUTE_PATH } from 'lib/paths'
import { redirectToPreviousPage } from 'lib/router'

import SeoHeader from 'components/SEOHeader'
import Avatar from 'components/Avatar'
import Nickname from 'components/Nickname'
import FollowButton from 'features/User/components/FollowButton'
import BoxHeader from 'components/BoxHeader'
import PostItemSkeleton from 'components/Post/PostItem/PostItemSkeleton'
import PostItem from 'components/Post/PostItem'

const PostShow = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { postId, isValidPostId, postRes, groupId, threadId } =
    useGroupOrThreadOrPostPageData([{ title: t('common.post') }])
  const isGroupPage = !isNil(groupId)
  const isThreadPage = !isNil(threadId)
  const [__domReady, __domReadySet] = useState(false)

  const [__posts, __postsSet] = useState<Array<Post>>([])

  const postActions = usePostActions({
    groupId: groupId,
    postList: __posts,
    setter: __postsSet,
    onReplyCallback: () => {
      postRes?.refetch?.()
    },
    onDeleteCallback: () => {
      redirectToPreviousPage()
    },
  })

  const postLikersRes = useGetPostLikersQuery({ postId })

  useEffect(() => {
    if (postRes?.isSuccess && !postRes?.isFetching && postRes?.currentData) {
      __postsSet([postRes?.currentData?.post])
    }
    {
      /* thread header already have back button */
    }
  }, [postRes])

  // post access permission check
  useEffect(() => {
    // Handle when invalid post id
    // TODO: maybe show not found UI or redirect to 404 page -> ask PO
    if (!isValidPostId || postRes?.status === 'rejected') {
      const target = groupId
        ? threadId
          ? ROUTE_PATH.GROUP_THREAD(groupId, threadId)
          : ROUTE_PATH.GROUP(groupId)
        : ROUTE_PATH.HOME

      navigate(target, {
        replace: true,
      })
      return
    }

    if (!postRes?.isSuccess) return

    // Handle when id in post response are not the same with in url
    const postGroupId = postRes.data?.post?.group_id ?? 0
    const postThreadId = postRes.data?.post?.thread_id ?? 0

    if (+groupId) {
      // incorrect group id -> force to group page
      if (+groupId !== postGroupId) {
        navigate(ROUTE_PATH.GROUP(groupId), { replace: true })
        return
      }
    } else {
      // access group post directly -> force to correct page
      if (postGroupId) {
        const target = postThreadId
          ? ROUTE_PATH.GROUP_THREAD_POST(postGroupId, postThreadId, postId)
          : matchI18nPath(ROUTE_PATH.POST_REPOST())
          ? ROUTE_PATH.GROUP_POST_REPOST(postGroupId, postId)
          : ROUTE_PATH.GROUP_POST(postGroupId, postId)

        navigate(target, { replace: true })
        return
      }
    }

    if (+threadId) {
      // incorrect thread id -> force to thread page
      if (+threadId !== postThreadId) {
        navigate(ROUTE_PATH.GROUP_THREAD(groupId, threadId), { replace: true })
        return
      }
    } else {
      // access thread post directly -> force to correct page
      if (postThreadId) {
        navigate(
          ROUTE_PATH.GROUP_THREAD_POST(postGroupId, postThreadId, postId),
          { replace: true }
        )
        return
      }
    }
  }, [groupId, threadId, postId, isValidPostId, postRes])

  // prevent react portal error when target not found
  useEffect(() => {
    __domReadySet(true)
  }, [])

  if (!__domReady && isThreadPage) return null
  return (
    <Fragment>
      <SeoHeader
        description={st(
          isGroupPage ? 'post_group.description' : 'post.description',
          {
            post_content: postRes?.data?.post?.text,
            username: postRes?.data?.post?.user?.nickname,
            post_date: dayjs(postRes?.data?.post?.updated_at * 1000).format(
              'YYYY/MM/DD HH:mm:ss'
            ),
            circle_name: postRes?.data?.group?.topic,
          }
        )}
        title={st(isGroupPage ? 'post_group.title' : 'post.title', {
          post_content: postRes?.data?.post?.text,
          username: postRes?.data?.post?.user?.nickname,
        })}
      />

      {(() => {
        const element = (
          <div className="PostList">
            {(!groupId || +groupId === __posts?.[0]?.group_id) &&
              __posts?.map?.((x, i) => (
                <PostItem
                  key={x?.id}
                  post={x}
                  index={i}
                  group={{ data: postRes?.data?.group }}
                  isLast
                  noLink
                  showReposts
                  {...postActions}
                />
              ))}

            {postRes?.isFetching && <PostItemSkeleton />}
          </div>
        )

        return isThreadPage ? (
          createPortal(element, document.querySelector('.GroupThread'))
        ) : (
          <div className="Box Timeline">
            <BoxHeader
              nav={[
                {
                  label: t('common.back'),
                  isBack: true,
                  onClick: (e) => {
                    e.preventDefault()
                    redirectToPreviousPage()
                  },
                },
              ]}
            />
            {element}
          </div>
        )
      })()}

      {(postLikersRes?.data?.users?.length ?? 0) > 0 && (
        <div className="Box">
          <div className="Box__header">
            <ul className="Box__nav">
              <li>
                <span className="Box__nav__item">
                  {t('post_page.post_likers')}
                </span>
              </li>
            </ul>
          </div>

          <dl className="ConfirmBox__users ConfirmBox__users--full">
            {(postLikersRes?.data?.users ?? [])?.map?.((postLiker, i) => (
              <div
                className={cx('ConfirmBox__users__item', {
                  'ConfirmBox__users__item--wide': true,
                  'ConfirmBox__users__item--no-bdb':
                    i == postLikersRes?.data?.users?.length - 1,
                })}
                key={i}
              >
                <Link
                  onClick={() => {
                    logEvent.user_profile_open({
                      target_user_id: postLiker?.id.toString(),
                      from: 'undefined',
                    })
                  }}
                  to={ROUTE_PATH.USER(postLiker?.id)}
                >
                  <Avatar user={postLiker} />
                </Link>

                <dt>
                  <Link
                    onClick={() => {
                      logEvent.user_profile_open({
                        target_user_id: postLiker?.id.toString(),
                        from: 'undefined',
                      })
                    }}
                    to={ROUTE_PATH.USER(postLiker?.id)}
                  >
                    <Nickname user={postLiker} />
                  </Link>
                </dt>

                <FollowButton user={postLiker} />
              </div>
            ))}
          </dl>
        </div>
      )}
    </Fragment>
  )
}

export default PostShow
